// API utility functions

// Base URL for API calls - will be different in development vs production
const API_BASE_URL = import.meta.env.PROD
  ? 'https://ayite-dj.vercel.app/api' // Production backend URL
  : 'http://localhost:3001/api'; // Development backend URL

// Direct backend URL as fallback
const DIRECT_BACKEND_URL = 'https://ayite-dj.vercel.app/';

// Log the current environment and API URLs for debugging
console.log('Current environment:', import.meta.env.PROD ? 'production' : 'development');
console.log('Using API URL:', API_BASE_URL);
console.log('Using fallback URL:', DIRECT_BACKEND_URL);

/**
 * Process events data from the API
 * @param {Array} data - The raw events data
 * @returns {Array} - The processed events data
 */
const processEventsData = (data) => {
  return data.map(event => ({
    id: event.id,
    legacyId: event.legacyId,
    name: event.name,
    slug: event.slug,
    startingTime: event.startingTime,
    coverUrl: event.coverUrl,
    venue: {
      name: event.venue.name,
      city: {
        name: event.venue.city.name,
        slug: event.venue.city.slug || event.venue.city.name.toLowerCase(),
        country: {
          name: event.venue.city.country.name
        }
      }
    }
  }));
};

/**
 * Fetch events from the Xceed API via our backend proxy
 * @returns {Promise<Object>} The events data
 */
export const fetchXceedEvents = async () => {
  try {
    // Try the standard API endpoint first
    try {
      const response = await fetch(`${API_BASE_URL}/xceed/events`);

      if (response.ok) {
        const data = await response.json();
        return processEventsData(data);
      }
    } catch (e) {
      console.log('Standard API endpoint failed, trying direct backend URL');
    }

    // If that fails, try the direct backend URL
    const directResponse = await fetch(`${DIRECT_BACKEND_URL}/api/xceed/events`);

    if (!directResponse.ok) {
      throw new Error(`API error: ${directResponse.status}`);
    }

    const data = await directResponse.json();
    return processEventsData(data);
  } catch (error) {
    console.error('Error fetching Xceed events:', error);
    throw error;
  }
};

/**
 * Fetch a specific event from the Xceed API via our backend proxy
 * @param {string} id - The event ID
 * @returns {Promise<Object>} The event data
 */
export const fetchXceedEvent = async (id) => {
  try {
    // Try the standard API endpoint first
    try {
      const response = await fetch(`${API_BASE_URL}/xceed/events/${id}`);

      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      console.log('Standard API endpoint failed, trying direct backend URL');
    }

    // If that fails, try the direct backend URL
    const directResponse = await fetch(`${DIRECT_BACKEND_URL}/api/xceed/events/${id}`);

    if (!directResponse.ok) {
      throw new Error(`API error: ${directResponse.status}`);
    }

    return await directResponse.json();
  } catch (error) {
    console.error(`Error fetching Xceed event ${id}:`, error);
    throw error;
  }
};

/**
 * Check if the backend API is available
 * @returns {Promise<boolean>} True if the API is available
 */
export const checkApiHealth = async () => {
  try {
    console.log('Checking API health...');

    // Try the standard health endpoint first
    try {
      console.log('Trying standard health endpoint:', `${API_BASE_URL}/health`);
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        cache: 'no-store'
      });

      console.log('Standard health endpoint response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Health check data:', data);
        return true;
      }
    } catch (e) {
      console.log('Standard health endpoint failed:', e.message);
      console.log('Trying direct backend URL');
    }

    // If that fails, try the direct backend URL
    try {
      console.log('Trying direct backend URL:', `${DIRECT_BACKEND_URL}/health`);
      const directResponse = await fetch(`${DIRECT_BACKEND_URL}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        cache: 'no-store'
      });

      console.log('Direct health endpoint response status:', directResponse.status);

      if (directResponse.ok) {
        const data = await directResponse.json();
        console.log('Direct health check data:', data);
        return true;
      }
    } catch (e) {
      console.log('Direct health endpoint failed:', e.message);
    }

    return false;
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};

/**
 * Fetch the latest YouTube shorts from AYITE's channel
 * @returns {Promise<Array>} Array of YouTube short video objects
 */
export const fetchYoutubeShorts = async () => {
  try {
    // Try the standard API endpoint first
    try {
      const response = await fetch(`${API_BASE_URL}/youtube/shorts`);

      if (response.ok) {
        const data = await response.json();
        // Return only the last 3 shorts
        return data.shorts.slice(0, 3);
      }
    } catch (e) {
      console.log('Standard YouTube API endpoint failed, trying direct backend URL');
    }

    // If that fails, try the direct backend URL
    const directResponse = await fetch(`${DIRECT_BACKEND_URL}/api/youtube/shorts`);

    if (!directResponse.ok) {
      throw new Error(`API error: ${directResponse.status}`);
    }

    const data = await directResponse.json();
    // Return only the last 3 shorts
    return data.shorts.slice(0, 3);
  } catch (error) {
    console.error('Error fetching YouTube shorts:', error);

    // Fallback data in case of error
    return [
      { id: 'eLtphwetAzw', embedUrl: 'https://www.youtube.com/embed/eLtphwetAzw' },
      { id: 'pXMNNxojqHc', embedUrl: 'https://www.youtube.com/embed/pXMNNxojqHc' },
      { id: 'WXhgKgCd-PE', embedUrl: 'https://www.youtube.com/embed/WXhgKgCd-PE' }
    ];
  }
};
